"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { OutlineItem } from "@/lib/markdownOutline";

/**
 * Right-side TOC for the doc preview.
 *
 * Reads heading ids that <Markdown> already emitted into the DOM and scrolls
 * to them on click. Scroll-spy via a single IntersectionObserver picks the
 * topmost intersecting heading as active. (PAUT-71)
 */
export function DocOutline({
  outline,
  scrollRoot,
}: {
  outline: OutlineItem[];
  /**
   * The actual overflow-y-auto element containing the rendered doc body.
   * IntersectionObserver needs this as `root` — DocsView wraps the preview in
   * its own scroll container, not the viewport, so observing against `null`
   * would never fire.
   */
  scrollRoot: HTMLElement | null;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (outline.length < 2 || !scrollRoot) return;
    const nodes = outline
      .map((o) => scrollRoot.querySelector<HTMLElement>(`#${CSS.escape(o.id)}`))
      .filter((n): n is HTMLElement => !!n);
    if (nodes.length === 0) return;
    // Active band sits in the upper portion of the scroll container. A
    // heading becomes active once it passes the 80px top edge and stays
    // active until the next heading takes over. The lower `-60%` margin lets
    // the LAST heading remain active even when fully above the band.
    const io = new IntersectionObserver(
      (entries) => {
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .map((e) => e.target as HTMLElement);
        if (intersecting.length === 0) return;
        intersecting.sort(
          (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
        );
        setActiveId(intersecting[0].id);
      },
      { root: scrollRoot, rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [outline, scrollRoot]);

  if (outline.length < 2) return null;
  const minLevel = Math.min(...outline.map((o) => o.level));

  return (
    <aside
      className="w-[220px] shrink-0 hidden lg:block border-l border-[var(--border)] px-3 py-4 sticky top-0 self-start max-h-screen overflow-y-auto scrollbar-thin"
      aria-label="On this page"
    >
      <div className="text-[11px] tracking-wider text-[var(--fg-tertiary)] uppercase mb-2 font-medium">
        On this page
      </div>
      <nav className="flex flex-col">
        {outline.map((item) => {
          const isActive = activeId === item.id;
          return (
            <button
              type="button"
              key={item.id}
              onClick={(e) => {
                e.preventDefault();
                const el = scrollRoot?.querySelector<HTMLElement>(
                  `#${CSS.escape(item.id)}`
                );
                if (!el || !scrollRoot) return;
                // Compute offset within the scroll container — scrollIntoView
                // on a nested scroll context still respects ancestors, but
                // setting scrollTop is more predictable and keeps the 64px
                // breathing room above the heading.
                const top =
                  el.getBoundingClientRect().top -
                  scrollRoot.getBoundingClientRect().top +
                  scrollRoot.scrollTop -
                  64;
                scrollRoot.scrollTo({ top, behavior: "smooth" });
              }}
              style={{ paddingLeft: 8 + (item.level - minLevel) * 10 }}
              className={cn(
                "block w-full text-left truncate py-0.5 text-[12px] transition-colors font-medium",
                isActive
                  ? "text-[var(--accent)] border-l-2 border-[var(--accent)] -ml-[2px]"
                  : "text-[var(--fg-secondary)] hover:text-[var(--fg)]"
              )}
              title={item.text}
            >
              {item.text}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
